import { createBrowserRouter, Navigate } from "react-router-dom";
import { makeRoute as route } from "@shared/utils/makeRoute";
import NotFoundPage from "@shared/pages/NotFoundPage";
import DashboardProgramPage from "./features/dashboard/pages/DashboardProgramPage";
import DashboardSettingsPage from "./features/dashboard/pages/DashboardSettingsPage";
import DashboardSupportHomePage from "./features/dashboard/pages/DashboardSupportHomePage";
import DashboardSupportArticlePage from "./features/dashboard/pages/DashboardSupportArticlePage";
import DashboardLayout from "./layouts/DashboardLayout";
import MarketingHome from "./features/marketing/pages/MarketingHome";
import MarketingLayout from "./layouts/MarketingLayout";
import OnboardingLayout from "./layouts/OnboardingLayout";
import OnboardingEnrollPage from "./features/onboarding/pages/OnboardingEnrollPage";
import OnboardingWaitlistPage from "./features/onboarding/pages/OnboardingWaitlistPage";
import OnboardingSignupSuccessPage from "./features/onboarding/pages/OnboardingSignupSuccessPage";
import OnboardingMatchingQuizPage from "./features/onboarding/pages/OnboardingMatchingQuizPage";
import OnboardingInviteFriendPage from "./features/onboarding/pages/OnboardingInviteFriendPage";
import OnboardingInviteInstitutionPage from "./features/onboarding/pages/OnboardingInviteInstitutionPage";
import OnboardingOnboardingCompletedPage from "./features/onboarding/pages/OnboardingOnboardingCompletedPage";
import OnboardingSignupContainer from "./features/onboarding/components/OnboardingSignup/OnboardingSignupContainer";
import VideoLayout from "./layouts/VideoLayout";
import VideoEndLayout from "./layouts/VideoEndLayout";
import VideoJoinPage from "./features/video/pages/VideoJoinPage";
import VideoMeetingPage from "./features/video/pages/VideoMeetingPage";
import VideoEndedPage from "./features/video/pages/VideoEndedPage";
import PlatformLoginPage from "./features/auth/pages/PlatformLoginPage";
import PlatformLogoutPage from "./features/auth/pages/PlatformLogoutPage";
import PlatformForgotPasswordPage from "./features/auth/pages/PlatformForgotPasswordPage";
import PlatformResetPasswordPage from "./features/auth/pages/PlatformResetPasswordPage";
import StaticPageLayout from "./layouts/StaticPageLayout";
import StaticPageContainer from "./features/support/components/StaticPage/StaticPageContainer";
import SupportLayout from "./layouts/SupportLayout";
import AuthLayout from "./layouts/AuthLayout";
import AboutPage from "./features/marketing/pages/AboutPage";
import ContactPage from "./features/marketing/pages/ContactPage";

import NotificationsPage from "./features/notifications/pages/NotificationsPage";
import { useSignupDisabled } from "./hooks/useSignupDisabled";

// Wrapper component to handle signup disabled state
function SignupRouteWrapper() {
  const isSignupDisabled = useSignupDisabled();

  if (isSignupDisabled) {
    return <Navigate to="/" replace />;
  }

  return <OnboardingSignupContainer />;
}

// Wrapper component to handle onboarding routes when signup is disabled
function OnboardingRouteWrapper({ children }: { children: React.ReactNode }) {
  const isSignupDisabled = useSignupDisabled();

  if (isSignupDisabled) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export const router = createBrowserRouter([
  // Marketing
  route("/", <MarketingHome />, <MarketingLayout title="Home" />),
  route(
    "/programs",
    <Navigate to="/#program" replace />,
    <MarketingLayout title="Programs" />
  ),
  route(
    "/how-it-works",
    <Navigate to="/#how-it-works" replace />,
    <MarketingLayout title="How It Works" />
  ),
  route(
    "/team",
    <Navigate to="/#team" replace />,
    <MarketingLayout title="Team" />
  ),
  route(
    "/donate",
    <Navigate to="/#donate" replace />,
    <MarketingLayout title="Donate" />
  ),

  // Auth
  route(
    "/signup",
    <SignupRouteWrapper />,
    <OnboardingLayout title="Sign Up" />
  ),
  route("/login", <PlatformLoginPage />, <AuthLayout title="Login" />),
  route("/logout", <PlatformLogoutPage />, <AuthLayout title="Logout" />),
  route(
    "/forgot-password",
    <PlatformForgotPasswordPage />,
    <AuthLayout title="Forgot Password" />
  ),
  route(
    "/reset-password",
    <PlatformResetPasswordPage />,
    <AuthLayout title="Reset Password" />
  ),

  // Onboarding
  route(
    "/enroll",
    <OnboardingRouteWrapper>
      <OnboardingEnrollPage />
    </OnboardingRouteWrapper>,
    <OnboardingLayout title="Enroll" />
  ),
  route(
    "/waitlist",
    <OnboardingRouteWrapper>
      <OnboardingWaitlistPage />
    </OnboardingRouteWrapper>,
    <OnboardingLayout title="Waitlist" />
  ),
  route(
    "/youre-in",
    <OnboardingRouteWrapper>
      <OnboardingSignupSuccessPage />
    </OnboardingRouteWrapper>,
    <OnboardingLayout title="You're In" />
  ),
  route(
    "/onboarding",
    <OnboardingRouteWrapper>
      <OnboardingMatchingQuizPage />
    </OnboardingRouteWrapper>,
    <OnboardingLayout title="Onboarding" />
  ),
  route(
    "/invite",
    <OnboardingRouteWrapper>
      <OnboardingInviteFriendPage />
    </OnboardingRouteWrapper>,
    <OnboardingLayout title="Invite a Friend" />
  ),
  route(
    "/invite-institution",
    <OnboardingInviteInstitutionPage />,
    <OnboardingLayout title="Invite an Institution" />
  ),
  route(
    "/congratulations",
    <OnboardingRouteWrapper>
      <OnboardingOnboardingCompletedPage />
    </OnboardingRouteWrapper>,
    <OnboardingLayout title="Congratulations" />
  ),

  // Dashboard (Closed)
  route(
    "/dashboard",
    <DashboardProgramPage />,
    <DashboardLayout title="Dashboard" />
  ),
  route(
    "/account",
    <DashboardSettingsPage />,
    <DashboardLayout title="Your Account" />
  ),
  route(
    "/notifications",
    <NotificationsPage />,
    <DashboardLayout title="Notifications" />
  ),

  // Support
  route(
    "/support",
    <DashboardSupportHomePage />,
    <SupportLayout title="Support" />
  ),
  route(
    "/support/:articleId",
    <DashboardSupportArticlePage />,
    <SupportLayout title="Support" />
  ),

  // Video (Closed)
  route(
    "/video/join/:meetingId",
    <Navigate to="/video/join" replace />,
    <VideoLayout isWithNavigation />
  ),
  route("/video/join", <VideoJoinPage />, <VideoLayout isWithNavigation />),
  route("/video/meeting/:meetingId", <VideoMeetingPage />, <VideoLayout />),
  route(
    "/video/end/:meetingId",
    <VideoEndedPage />,
    <VideoEndLayout isWithNavigation />
  ),

  // Not Found
  route("/not-found", <NotFoundPage />, <StaticPageLayout title="Not Found" />),

  // Static Pages
  route("/about", <AboutPage />, <SupportLayout title="About" />),
  route("/contact", <ContactPage />, <SupportLayout title="Contact" />),

  route(
    "/terms",
    <Navigate to="/terms-of-service" replace />,
    <StaticPageLayout title="Terms of Service" />
  ),
  route("/:slug", <StaticPageContainer />, <StaticPageLayout title="" />),
]);
